import type { NodeProps } from '@xyflow/react';
import { Box } from 'lucide-react';
import { memo } from 'react';
import { getCatalogEntry } from '../../lib/architectureNodeCatalog';
import { useThemeStore } from '../../store/useThemeStore';

/** Relative luminance (0-1) of a #rrggbb colour. */
function luminance(hex: string): number {
  const v = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  const lin = (c: number) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin((v >> 16) & 255) + 0.7152 * lin((v >> 8) & 255) + 0.0722 * lin(v & 255);
}

export interface ServiceGroupNodeData {
  kind: string;
  label: string;
  color?: string;
  isSelected?: boolean;
  memberCount: number;
  tour?: 'focus' | 'dim' | null;
  [key: string]: unknown;
}

function ServiceGroupNode({ data }: NodeProps & { data: ServiceGroupNodeData }) {
  const entry = getCatalogEntry(data.kind);
  const Icon = entry?.icon.icon ?? Box;
  const color = data.color ?? entry?.color ?? '#6366f1';
  const theme = useThemeStore((s) => s.theme);
  const bgAlpha = theme === 'dark' ? '3d' : '26';
  // The group's icon keeps the node type's own brand colour (GitLab orange, Docker blue...) even when the box is
  // tinted something else, unless that colour would disappear into the background.
  const brand = entry?.color;
  const iconColor = brand && (theme === 'dark' ? luminance(brand) > 0.12 : luminance(brand) < 0.6) ? brand : color;

  return (
    <div
      className={`h-full w-full rounded-xl outline-none ${
        data.isSelected ? 'border-[3px] border-solid' : 'border-2 border-dashed'
      }`}
      style={{
        opacity: data.tour === 'dim' ? 0.3 : 1,
        transition: 'opacity 500ms',
        borderColor: color,
        background: `${color}${bgAlpha}`,
        boxShadow: data.isSelected ? `0 0 0 3px ${color}4d, 0 8px 20px ${color}45` : undefined,
      }}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color }}>
        <Icon className="h-4 w-4 shrink-0" style={{ color: iconColor }} />
        <span className="truncate">{data.label}</span>
        {data.memberCount === 0 && <span className="ml-auto lowercase italic opacity-70">drop nodes here</span>}
      </div>
    </div>
  );
}

export default memo(ServiceGroupNode);
